import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api-client';

import type { InvoiceDirection } from './use-applied-provisional';

// The server-suggested document number (PI-2026-01700-01): an editable prefill
// composed from the chain's shared core and predicted next position. Fetching
// pins the chain core on first ask, so it only runs while a create dialog is
// actually open (`enabled`).

export type SuggestableInvoiceType = 'Advance' | 'Provisional' | 'Final Settlement' | 'Adjustment';

export interface SuggestedInvoiceNumberParams {
  readonly invoiceType: SuggestableInvoiceType;
  readonly noteType?: 'Credit' | 'Debit';
  readonly direction: InvoiceDirection;
  readonly blOrderId?: string;
  readonly orderId?: string;
  readonly enabled: boolean;
}

interface SuggestedInvoiceNumberResponse {
  readonly invoice_number: string;
}

export function suggestedInvoiceNumberKey(params: SuggestedInvoiceNumberParams) {
  return [
    'invoice-number-suggestion',
    params.invoiceType,
    params.noteType ?? null,
    params.direction,
    params.blOrderId ?? null,
    params.orderId ?? null,
  ] as const;
}

export function useSuggestedInvoiceNumber(params: SuggestedInvoiceNumberParams) {
  const hasGrain =
    (params.blOrderId !== undefined && params.blOrderId.length > 0) ||
    (params.orderId !== undefined && params.orderId.length > 0);
  const search = new URLSearchParams({
    invoice_type: params.invoiceType,
    direction: params.direction,
  });
  if (params.noteType !== undefined) search.set('note_type', params.noteType);
  if (params.blOrderId !== undefined && params.blOrderId.length > 0) {
    search.set('bl_order_id', params.blOrderId);
  } else if (params.orderId !== undefined && params.orderId.length > 0) {
    search.set('order_id', params.orderId);
  }
  return useQuery<SuggestedInvoiceNumberResponse>({
    queryKey: suggestedInvoiceNumberKey(params),
    enabled: params.enabled && hasGrain,
    // Always re-derive on open: the next free position moves as invoices are
    // created, cancelled, or deleted, and a stale suggestion would collide with
    // an existing (e.g. cancelled) number on save.
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: () =>
      apiRequest<SuggestedInvoiceNumberResponse>(`/invoice-number-suggestion?${search.toString()}`),
  });
}
